import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { BuildingOfficeIcon, UserIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import api from '../services/api';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%);
  padding: var(--space-4);
`;

const RegisterCard = styled.div`
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: var(--space-6);
  
  svg {
    width: 100%;
    max-width: 300px;
    height: auto;
    margin-bottom: var(--space-2);
  }
  
  h1 {
    color: var(--primary-600);
    font-size: var(--font-size-3xl);
    font-weight: var(--font-extrabold);
    margin-bottom: var(--space-2);
  }
  
  p {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-600)'};
    font-size: var(--font-size-sm);
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const FormGroup = styled.div`
  position: relative;
  
  label {
    display: block;
    font-weight: var(--font-medium);
    color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : 'var(--gray-700)'};
    margin-bottom: var(--space-2);
    font-size: var(--font-size-sm);
  }
  
  select, input {
    width: 100%;
    padding: var(--space-3);
    padding-left: 2.75rem;
    border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : 'var(--gray-300)'};
    border-radius: 8px;
    font-size: var(--font-size-sm);
    transition: all 0.2s ease-in-out;
    box-sizing: border-box;
    padding-left: 35px !important;
    background-color: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
    color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'var(--gray-900)'};
    
    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }
    
    &::placeholder {
      color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-400)'};
    }
  }
  
  .helper-text {
    font-size: var(--font-size-xs);
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-500)'};
    margin-top: var(--space-1);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: var(--space-3);
  top: 2.375rem;
  color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-400)'};
  width: 1.25rem;
  height: 1.25rem;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const Message = styled.div`
  padding: var(--space-3);
  border-radius: 8px;
  font-size: var(--font-size-sm);
  text-align: center;
  
  &.success {
    background-color: ${({ theme }) => theme?.mode === 'dark' ? '#064e3b' : '#d4edda'};
    border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#065f46' : '#c3e6cb'};
    color: ${({ theme }) => theme?.mode === 'dark' ? '#6ee7b7' : '#155724'};
  }
  
  &.error {
    background-color: ${({ theme }) => theme?.mode === 'dark' ? '#7f1d1d' : 'var(--primary-50)'};
    border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#991b1b' : 'var(--primary-200)'};
    color: ${({ theme }) => theme?.mode === 'dark' ? '#fecaca' : 'var(--primary-800)'};
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: var(--space-4);
  font-size: var(--font-size-sm);
  color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : 'var(--gray-600)'};
  
  a {
    color: var(--primary-600);
    font-weight: var(--font-medium);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
`;

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tenantName: '',
    tenantSlug: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Auto-generate slug from tenant name
    if (name === 'tenantName') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 50);
      setFormData(prev => ({ ...prev, tenantSlug: slug }));
    }
    
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/register-tenant', {
        tenant_name: formData.tenantName,
        tenant_slug: formData.tenantSlug,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
      });

      setMessage({ 
        type: 'success', 
        text: 'Registration successful! Redirecting to login...' 
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            registered: true, 
            tenantSlug: formData.tenantSlug,
            email: formData.email 
          } 
        });
      }, 2000);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error || 'Registration failed. Please try again.' 
      });
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Logo>
          <svg width="300" height="60" viewBox="0 0 600 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <text 
              x="50%" 
              y="75%" 
              dominantBaseline="middle" 
              textAnchor="middle" 
              fontFamily="Impact, Haettenschweiler, 'Arial Black', sans-serif" 
              fontSize="80" 
              fontWeight="bold" 
              letterSpacing="5" 
              fill="var(--primary-600)">
              MARKETEER
            </text>
          </svg>
          <h1>Create Your Account</h1>
          <p>Register your organization</p>
        </Logo>

        {message.text && (
          <Message className={message.type}>
            {message.text}
          </Message>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="tenantName">Organization Name *</label>
            <Icon>
              <BuildingOfficeIcon />
            </Icon>
            <input
              type="text"
              id="tenantName"
              name="tenantName"
              value={formData.tenantName}
              onChange={handleChange}
              placeholder="e.g., Acme Corp"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="tenantSlug">Organization Slug *</label>
            <Icon>
              <BuildingOfficeIcon />
            </Icon>
            <input
              type="text"
              id="tenantSlug"
              name="tenantSlug"
              value={formData.tenantSlug}
              onChange={handleChange}
              placeholder="e.g., acme-corp"
              pattern="[a-z0-9-]+"
              required
            />
            <p className="helper-text">
              This will be used in your login URL (lowercase, numbers, hyphens only)
            </p>
          </FormGroup>

          <TwoColumnRow>
            <FormGroup>
              <label htmlFor="firstName">First Name *</label>
              <Icon>
                <UserIcon />
              </Icon>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="lastName">Last Name *</label>
              <Icon>
                <UserIcon />
              </Icon>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Doe"
                required
              />
            </FormGroup>
          </TwoColumnRow>

          <FormGroup>
            <label htmlFor="email">Email *</label>
            <Icon>
              <EnvelopeIcon />
            </Icon>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@acme-corp.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Password *</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="At least 6 characters"
              minLength="6"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <Icon>
              <LockClosedIcon />
            </Icon>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              minLength="6"
              required
            />
          </FormGroup>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-block"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </Form>

        <LoginLink>
          Already have an account? <Link to="/login">Sign in</Link>
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
}

export default Register;
